import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { buildExportQuery, scaricaFile } from '../lib/utils.ts';
import { enforcePerm } from '../lib/perm.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'tipo', label: 'Tipo', type: 'text' },
  { key: 'siglaOrigine', label: 'Ricevente', type: 'text' },
  { key: 'modalitaRicezione', label: 'Ricezione', type: 'text' },
  { key: 'cassa', label: 'Cassa', type: 'text' },
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}' },
  { key: 'lavoratore.codiceFiscale', label: 'CF', type: 'code' },
  { key: 'lavoratore.dataNascita', label: 'Nascita', type: 'date', emptyText: '-' },
  { key: 'dataDisdetta', label: 'Data disdetta', type: 'date', sortable: true },
  { key: 'periodoRiassociazione', label: 'Mese rilevazione', type: 'text' },
];

const ACTIONS = JSON.stringify([
  { name: 'elimina', icon: 'trash', btnClass: 'btn-outline-danger', title: 'Elimina disdetta' },
]);

/** Filtro rapido stato → parametri della lista */
const STATO_RAPIDO: Record<string, Record<string, string>> = {
  INVIATA: { tipo: 'INVIATA' },
  RICEVUTA: { tipo: 'RICEVUTA' },
  LIBERO: { tipo: 'GENERICO', sigla: 'NESSUNO' },
  ATTESA: { tipo: 'GENERICO', sigla: '_NULL' },
  RIASSOCIATA: { tipo: 'RIASSOCIATA' },
};

const CAMPI_SELECT = ['tipo', 'sigla', 'cassa', 'origine'] as const;
const CAMPI_DATA = ['da', 'a'] as const;
const CAMPI_MESE = ['periodo_da', 'periodo_a'] as const;

/** Input type="month" (YYYY-MM) → periodo YYYYMM e viceversa */
const meseToPeriodo = (v: string): string => v.replace('-', '');
const periodoToMese = (p: string): string => `${p.slice(0, 4)}-${p.slice(4, 6)}`;

/**
 * wc-tabella-disdette — pagina Disdette (preventivo 06/09/2026 punto 1).
 * Vista principale senza riassociate; filtro rapido RIASS o parametri URL
 * (?vista=riassociate&cassa=CE&periodo=YYYYMM) per la vista riassociate.
 */
export class TabellaDisdette extends AppComponent {
  static override componentName = 'wc-tabella-disdette';
  protected override get templateId() { return 'tabella-disdette'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private messageListenerKey: symbol | null = null;

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      actionsJson: ACTIONS,
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupExportExcel();
    this.setupBanner();
    this.setupMessageListener();
    if (!this.applicaParametriUrl()) this.caricaDisdette();
    this.caricaBanner();
  }

  // --- Setup ---

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-render-complete', () => enforcePerm(this.dataTable!));

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'modifica') this.handleModifica(row);
      if (action === 'elimina') this.handleElimina(row);
      if (action === 'crea-delega') this.handleCreaDelega(row);
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => this.offcanvas?.show());

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncStatoRapido();
        this.syncButtonGroup('cassa');
        this.currentPage = 1;
        this.caricaDisdette();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaDisdette();
    });
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      if (campo === 'stato-disdetta') {
        this.impostaStatoRapido(wasActive ? '' : value);
      } else {
        group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
        if (!wasActive) btn.classList.add('active');
        const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
        if (select) select.value = wasActive ? '' : value;
      }

      this.currentPage = 1;
      this.caricaDisdette();
    });
  }

  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const qs = buildExportQuery(this.raccogliFiltri(), this.currentFilter);
      scaricaFile(`/api/disdette/export-csv?${qs}`);
    });
  }

  private setupBanner(): void {
    this.delegate.on('click', '[data-action="vedi-riassociate"]', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="vedi-riassociate"]') as HTMLElement;
      this.mostraRiassociate(btn.dataset.cassa ?? '', btn.dataset.periodo ?? '');
    });
  }

  private setupMessageListener(): void {
    const handler = (e: MessageEvent) => {
      if (e.data?.action === 'modal-close' && e.data.refresh) {
        this.caricaDisdette();
        // La modifica può rivalutare la disdetta
        this.caricaBanner();
      }
    };
    this.messageListenerKey = this.listen(window, 'message', handler as EventListener);
  }

  /**
   * Link dal dettaglio elaborazione: /disdette?vista=riassociate&cassa=CE&periodo=YYYYMM.
   * Restituisce true se ha gia' avviato il caricamento della lista.
   */
  private applicaParametriUrl(): boolean {
    const url = new URLSearchParams(window.location.search);
    if (url.get('vista') !== 'riassociate') return false;
    this.mostraRiassociate(url.get('cassa') ?? '', url.get('periodo') ?? '');
    return true;
  }

  // --- Stato filtri ---

  private mostraRiassociate(cassa: string, periodo: string): void {
    this.resetFiltri();
    this.impostaStatoRapido('RIASSOCIATA');
    this.impostaValore('cassa', cassa);
    this.syncButtonGroup('cassa');
    if (periodo) {
      this.impostaValore('periodo_da', periodoToMese(periodo));
      this.impostaValore('periodo_a', periodoToMese(periodo));
    }
    this.currentPage = 1;
    this.caricaDisdette();
  }

  /**
   * Il filtro rapido stato e' una scorciatoia sulle select Tipo e Sindacato attuale
   * dell'offcanvas: impostarlo scrive le select, cosi' i filtri avanzati applicati
   * dopo (organizzazione, cassa, periodo) si sommano alla vista scelta.
   */
  private impostaStatoRapido(value: string): void {
    const params = STATO_RAPIDO[value] ?? {};
    this.impostaValore('tipo', params.tipo ?? '');
    this.impostaValore('sigla', params.sigla ?? '');
    this.evidenziaStatoRapido(value);
  }

  /** Dopo l'offcanvas: evidenzia il bottone rapido che corrisponde alle select, se esiste */
  private syncStatoRapido(): void {
    const tipo = this.valore('tipo');
    const sigla = this.valore('sigla');
    const attivo = Object.entries(STATO_RAPIDO)
      .find(([, p]) => p.tipo === tipo && (!p.sigla || p.sigla === sigla))?.[0] ?? '';
    this.evidenziaStatoRapido(attivo);
  }

  private evidenziaStatoRapido(value: string): void {
    const group = this.$('[data-filtro-rapido="stato-disdetta"]');
    group?.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', !!value && btn.dataset.value === value);
    });
  }

  private impostaValore(campo: string, value: string): void {
    const el = this.$(`[data-filtro-val="${campo}"]`) as HTMLInputElement | HTMLSelectElement | null;
    if (el) el.value = value;
  }

  private valore(campo: string): string {
    return (this.$(`[data-filtro-val="${campo}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
  }

  private syncButtonGroup(campo: string): void {
    const val = this.valore(campo);
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    group?.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    for (const campo of CAMPI_SELECT) {
      const val = this.valore(campo);
      if (val) params[campo] = val;
    }
    for (const campo of CAMPI_DATA) {
      const val = this.valore(campo);
      if (val) params[campo] = val;
    }
    for (const campo of CAMPI_MESE) {
      const val = this.valore(campo);
      if (val) params[campo] = meseToPeriodo(val);
    }

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    this.impostaStatoRapido('');
    this.syncButtonGroup('cassa');
  }

  private updateFiltriCount(): void {
    const count = Object.keys(this.raccogliFiltri()).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    this.$('[data-action="reset-filtri-toolbar"]')?.classList.toggle('d-none', count === 0);
  }

  // --- Caricamento dati ---

  private async caricaDisdette(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const result = await this.api.disdette.listaPaginata({
      ...filtri,
      nascosta: '0',
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { disdette, count, totalPages, pageSize, page } = result.data;
    this.dataTable.setData(disdette as unknown as Record<string, unknown>[], {
      page, totalPages, count, pageSize,
    });
  }

  private async caricaBanner(): Promise<void> {
    const container = this.$('[data-banner-riassociazioni]');
    if (!container) return;
    const result = await this.api.disdette.riepilogoRiassociazioni();
    if (!result.ok || result.data.riepilogo.length === 0) {
      container.innerHTML = '';
      return;
    }
    templateParseToElement('tabella-disdette-banner', container, { riepilogo: result.data.riepilogo });
  }

  // --- Azioni riga ---

  private handleCreaDelega(row: Record<string, unknown>): void {
    const id = row.id as number;
    const cassa = row.cassa as string;
    window.location.href = `/deleghe/nuova?fromDisdetta=${id}&defaultCassa=${cassa}`;
  }

  /** La lista si ricarica dal messaggio `modal-close` dell'iframe (come per il nuovo inserimento) */
  private handleModifica(row: Record<string, unknown>): void {
    void Weext.modalIframe(`/disdette/${row.id}/modifica?layout=modal`, {
      title: 'Modifica disdetta',
      size: 'lg',
    });
  }

  private async handleElimina(row: Record<string, unknown>): Promise<void> {
    const id = row.id as number;
    const tipo = row.tipo as string;
    const sigla = (row.sigla as string) || 'generico';
    const lav = row.lavoratore as { cognome: string; nome: string } | null;
    const nome = lav ? `${lav.cognome} ${lav.nome}` : 'sconosciuto';

    const ok = await Weext.confirm(
      `Eliminare la disdetta ${tipo} ${sigla} per ${nome}?`,
      { title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger' },
    );
    if (!ok) return;

    const result = await this.api.disdette.elimina(id);
    if (result.ok) {
      Weext.toast('Disdetta eliminata', { type: 'success' });
      this.caricaDisdette();
      this.caricaBanner();
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    if (this.messageListenerKey) {
      this.unlisten(this.messageListenerKey);
      this.messageListenerKey = null;
    }
  }
}
